/**
 * /docs — Documentation hub.
 *
 * Categorised landing page linking to operator, trustee, and developer
 * documentation. Top categories link to their own sub-pages at /docs/$slug.
 *
 * Replaces the Phase 9 ComingSoonShell stub per Phase 3 Task 3.7 of the
 * 2026-05-16 implementation plan.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MarketingLayout } from '../components/layouts/marketing-layout';

export const Route = createFileRoute('/docs')({
  component: DocsPage,
});

interface DocSection {
  eyebrow: string;
  heading: string;
  blurb: string;
  links: Array<{ label: string; to?: string; href?: string; note?: string }>;
}

const SECTIONS: DocSection[] = [
  {
    eyebrow: 'Getting started',
    heading: 'Set up your tenant in an afternoon',
    blurb:
      'A guided path from first login to first trust onboarded. Designed for the senior administrator commissioning the platform.',
    links: [
      { label: 'Tenant setup checklist', to: '/docs/getting-started' },
      { label: 'Branding the tenant (subdomain, colours, sender)', note: 'In-product guide' },
      { label: 'Inviting administrators and assigning roles',     note: 'In-product guide' },
    ],
  },
  {
    eyebrow: 'Trust lifecycle',
    heading: 'Onboard, sign, distribute',
    blurb:
      'The full lifecycle of a trust on Elreign. Covers the deed-to-first-resolution flow and the recurring cycle of resolutions and distributions.',
    links: [
      { label: 'Onboarding a trust',     to: '/docs/onboarding-a-trust' },
      { label: 'The signing flow',       to: '/docs/signing-flow' },
      { label: 'Distribution payments',  note: 'In-product guide' },
      { label: 'Distribution tax certificates', note: 'In-product guide' },
    ],
  },
  {
    eyebrow: 'Compliance',
    heading: 'POPIA, FICA, and the Master',
    blurb:
      'Day-to-day reference for the FICA and POPIA obligations the platform supports, including DSAR fulfilment and Master ICMS lodgement.',
    links: [
      { label: 'POPIA · POPIA compliance posture', to: '/compliance/popia' },
      { label: 'FICA · FICA compliance posture',   to: '/compliance/fica' },
      { label: 'DSAR fulfilment',                  note: 'In-product guide' },
      { label: 'Master ICMS lodgement',            note: 'In-product guide' },
    ],
  },
  {
    eyebrow: 'API reference',
    heading: 'For practice management integrators',
    // MKT-01 (ruling 8, 2026-09-03; finding D2). There is no verifier
    // specification to request: the hash format is not written down anywhere a
    // stranger can read, which is what product.tsx now says in as many words.
    // What a firm CAN ask for is the signing key and an export's manifest.
    blurb:
      'Webhook payloads, the signing key and manifest that let you recheck an audit export yourself, and read-only API endpoints for integration with practice management systems.',
    links: [
      { label: 'Audit export signing key and manifest', note: 'On request from support@elreign.io' },
      { label: 'Webhook payloads',                   note: 'PDF · request from support@elreign.io' },
      { label: 'Read-only trust register API',       note: 'PDF · request from support@elreign.io' },
    ],
  },
];

function DocsPage() {
  useEffect(() => {
    document.title = 'Documentation · Elreign: operator, trustee, and developer guides';
    const meta = document.querySelector('meta[name="description"]') ?? (() => {
      const m = document.createElement('meta');
      m.name = 'description';
      document.head.appendChild(m);
      return m;
    })();
    meta.setAttribute(
      'content',
      'Documentation for Elreign trust administration: getting started, trust lifecycle (onboarding, signing, distributions), compliance (POPIA, FICA), and API reference.',
    );
  }, []);

  return (
    <MarketingLayout>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '72px 24px 96px' }}>
        {/* Hero */}
        <header style={{ marginBottom: 56, textAlign: 'center' }}>
          <p className="eyebrow" style={{ marginBottom: 18 }}>Documentation</p>
          <h1
            className="serif"
            style={{
              fontSize: 'clamp(36px, 5.5vw, 56px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            Everything an administrator <em>needs.</em>
          </h1>
          <p
            style={{
              fontSize: 18,
              color: 'var(--ink-3)',
              lineHeight: 1.65,
              maxWidth: 600,
              margin: '0 auto',
            }}
          >
            Public guides for getting started, running the trust lifecycle on the platform, and
            meeting your POPIA and FICA obligations. Tenant-specific guides are inside the
            product, signed in to your firm subdomain.
          </p>
        </header>

        {/* Sections */}
        <section
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 24,
          }}
        >
          {SECTIONS.map((s) => (
            <article
              key={s.heading}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--hairline)',
                borderRadius: 'var(--r-xl)',
                padding: '28px 30px 26px',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              <div>
                <p className="eyebrow" style={{ marginBottom: 10 }}>
                  {s.eyebrow}
                </p>
                <h2
                  className="serif"
                  style={{
                    fontSize: 22,
                    fontStyle: 'italic',
                    fontWeight: 300,
                    color: 'var(--ink)',
                    margin: '0 0 12px',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                  }}
                >
                  {s.heading}
                </h2>
                <p style={{ fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6, margin: 0 }}>
                  {s.blurb}
                </p>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {s.links.map((l) => (
                  <li key={l.label}>
                    {l.to ? (
                      <Link
                        to={l.to}
                        style={{
                          fontSize: 14,
                          color: 'var(--primary-600)',
                          textDecoration: 'none',
                          borderBottom: '1px solid rgba(31,77,69,0.2)',
                          paddingBottom: 1,
                        }}
                      >
                        {l.label}
                      </Link>
                    ) : l.href ? (
                      <a
                        href={l.href}
                        style={{
                          fontSize: 14,
                          color: 'var(--primary-600)',
                          textDecoration: 'none',
                          borderBottom: '1px solid rgba(31,77,69,0.2)',
                          paddingBottom: 1,
                        }}
                      >
                        {l.label}
                      </a>
                    ) : (
                      <span style={{ fontSize: 14, color: 'var(--ink-2)' }}>
                        {l.label}
                        {l.note && (
                          <span
                            className="mono"
                            style={{
                              fontSize: 11,
                              color: 'var(--ink-4)',
                              marginLeft: 8,
                            }}
                          >
                            {l.note}
                          </span>
                        )}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </section>

        {/* Contact note */}
        <p
          style={{
            marginTop: 56,
            paddingTop: 32,
            borderTop: '1px solid var(--hairline)',
            fontSize: 14,
            color: 'var(--ink-3)',
            lineHeight: 1.6,
            textAlign: 'center',
          }}
        >
          Cannot find what you are looking for? Write to{' '}
          <a href="mailto:support@elreign.io" style={{ color: 'var(--primary-600)' }}>
            support@elreign.io
          </a>. We answer within one business day.
        </p>
      </div>
    </MarketingLayout>
  );
}
